import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import WithDefaults from "@mirage/storage/with-defaults";
import { v5 as uuidv5 } from "uuid";

import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import type { QuerySuggestionsOptions } from "@mirage/service-query-suggestions/types";
import type { GuaranteedKVStorage, KVStorage } from "@mirage/storage";

const CACHE_NAMESPACE = "6ba7b810-9dad-11d1-80b4-00c04fd430c8";
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
export const DEFAULT_MAX_SUGGESTIONS_PER_CACHE = 50; // Max suggestions per subcache
const DEFAULT_REFRESH_PERCENTAGE = 0.25; // Replace 25% of old suggestions by default
const DEFAULT_MIN_REFRESH_AGE_MS = 12 * 60 * 60 * 1000; // 12 hours min age for refresh
const LAST_SYNC_KEY = "last_sync_timestamp";
export const GLOBAL_CACHE_KEY = "global";

const logger = tagged("service-query-suggestions/cache");
const metrics = namespace("query-suggestions-cache");

export type CachedRequestOptions = {
  // Should we hydrate the cache when we have to fallback to fetching from the server?
  shouldHydrateCache?: boolean;

  // How many results do we need in the cache to consider it useful?
  cacheCapacityUsefulnessThreshold?: number;

  // NOTE: If there is a need for more explicit manual control over the cache key,
  // customCacheKey could be added here to provide a custom override of the generated
  // deterministic cache key.
  // It can then be propagated through the top-level service functions as needed.
  // customCacheKey?: string;
};

export type CacheKeyUnion = typeof GLOBAL_CACHE_KEY | string;

interface TimestampedQuerySuggestionResult extends QuerySuggestionResult {
  _timestamp: number;
}

export interface QuerySuggestionsCacheShape {
  scopes: {
    [k: string]: TimestampedQuerySuggestionResult[];
  };
  [LAST_SYNC_KEY]?: number;
}

export class QuerySuggestionsCache {
  private cache: GuaranteedKVStorage<QuerySuggestionsCacheShape>;
  private ttlMs: number;
  private maxSuggestionsPerCache: number;
  private refreshPercentage: number;
  private minRefreshAgeMs: number;

  public static deriveCacheKey(
    options: QuerySuggestionsOptions,
  ): CacheKeyUnion {
    if (
      !options.userQuery &&
      (!options.documentIds || options.documentIds.length === 0)
    ) {
      return GLOBAL_CACHE_KEY;
    }

    const input = JSON.stringify({
      query: options.userQuery?.trim().toLowerCase() || "",
      docIds: options.documentIds || [],
      stackId: options.stackId,
    });

    return uuidv5(input, CACHE_NAMESPACE);
  }

  constructor(
    rawStorage: KVStorage<QuerySuggestionsCacheShape>,
    ttlMs: number = DEFAULT_TTL_MS,
    maxSuggestionsPerCache: number = DEFAULT_MAX_SUGGESTIONS_PER_CACHE,
    refreshPercentage: number = DEFAULT_REFRESH_PERCENTAGE,
    minRefreshAgeMs: number = DEFAULT_MIN_REFRESH_AGE_MS,
  ) {
    const defaults: QuerySuggestionsCacheShape = {
      scopes: {
        [GLOBAL_CACHE_KEY]: [],
      },
    };

    this.cache = new WithDefaults(rawStorage, defaults);
    this.ttlMs = ttlMs;
    this.maxSuggestionsPerCache = maxSuggestionsPerCache;
    this.refreshPercentage = refreshPercentage;
    this.minRefreshAgeMs = minRefreshAgeMs;
  }

  private filterExpiredSuggestions(
    suggestions: TimestampedQuerySuggestionResult[],
  ): TimestampedQuerySuggestionResult[] {
    const now = Date.now();
    const filtered = suggestions.filter((suggestion) => {
      const isValid = now - suggestion._timestamp <= this.ttlMs;
      if (!isValid) {
        logger.debug(`Filtered out expired suggestion: ${suggestion.text}`);
      }
      return isValid;
    });

    if (filtered.length !== suggestions.length) {
      metrics.counter("expired-filtered", suggestions.length - filtered.length);
    }

    return filtered;
  }

  private applyFIFO(
    suggestions: TimestampedQuerySuggestionResult[],
  ): TimestampedQuerySuggestionResult[] {
    if (suggestions.length <= this.maxSuggestionsPerCache) {
      return suggestions;
    }

    // Sort by timestamp (oldest first)
    const sorted = [...suggestions].sort((a, b) => a._timestamp - b._timestamp);
    const toKeep = sorted.slice(-this.maxSuggestionsPerCache);

    logger.debug(
      `FIFO eviction: removed ${sorted.length - toKeep.length} suggestions`,
    );

    metrics.counter("fifo-evicted", sorted.length - toKeep.length);

    return toKeep;
  }

  private dedupeSuggestions(
    suggestions: TimestampedQuerySuggestionResult[],
  ): TimestampedQuerySuggestionResult[] {
    const uniqueSuggestions = new Map<
      string,
      TimestampedQuerySuggestionResult
    >();

    for (const suggestion of suggestions) {
      const existingSuggestion = uniqueSuggestions.get(suggestion.text);

      if (
        !existingSuggestion ||
        existingSuggestion._timestamp < suggestion._timestamp
      ) {
        uniqueSuggestions.set(suggestion.text, suggestion);
      }
    }

    const result = Array.from(uniqueSuggestions.values());

    if (result.length !== suggestions.length) {
      metrics.counter("duplicates-removed", suggestions.length - result.length);
      logger.debug(
        `Removed ${suggestions.length - result.length} duplicate suggestions`,
      );
    }

    return result;
  }

  private applyStaggeredRefresh(
    existing: TimestampedQuerySuggestionResult[],
    newItems: TimestampedQuerySuggestionResult[],
  ): TimestampedQuerySuggestionResult[] {
    if (existing.length === 0) {
      return newItems;
    }

    if (newItems.length === 0) {
      return existing;
    }

    const now = Date.now();

    // Find items old enough to be refreshed
    const refreshCandidates = existing.filter(
      (item) => now - item._timestamp > this.minRefreshAgeMs,
    );
    const refreshCount = Math.ceil(
      refreshCandidates.length * this.refreshPercentage,
    );

    if (refreshCount <= 0) {
      // Nothing to refresh yet, just combine and dedupe
      logger.debug("No suggestions old enough for staggered refresh");
      return this.dedupeSuggestions([...existing, ...newItems]);
    }

    // Sort existing by age (oldest first)
    const sortedExisting = [...existing].sort(
      (a, b) => a._timestamp - b._timestamp,
    );
    const keepExisting = sortedExisting.slice(refreshCount);
    const combined = [...keepExisting, ...newItems];
    const deduped = this.dedupeSuggestions(combined);

    metrics.counter("staggered-refresh-count", refreshCount);
    logger.debug(
      `Staggered refresh: replaced ${refreshCount} of ${existing.length} suggestions`,
    );

    return deduped;
  }

  private async getScopes(): Promise<
    Record<string, TimestampedQuerySuggestionResult[]>
  > {
    const cacheState = await this.cache.getAll();
    return cacheState.scopes || {};
  }

  private async updateScopes(
    scopes: Record<string, TimestampedQuerySuggestionResult[]>,
  ): Promise<void> {
    await this.cache.set("scopes", scopes);
  }

  public async get(cacheKey: CacheKeyUnion): Promise<QuerySuggestionResult[]> {
    const scopes = await this.getScopes();
    const timestampedSuggestions = scopes[cacheKey] || [];

    if (!timestampedSuggestions.length) {
      metrics.counter("cache-miss", 1, {
        key_type: cacheKey === GLOBAL_CACHE_KEY ? GLOBAL_CACHE_KEY : `specific`,
      });
      return [];
    }

    metrics.counter("cache-hit", 1, {
      key_type: cacheKey === GLOBAL_CACHE_KEY ? GLOBAL_CACHE_KEY : `specific`,
    });
    metrics.stats("cache-size", timestampedSuggestions.length);

    // Filter out expired suggestions
    const validSuggestions = this.filterExpiredSuggestions(
      timestampedSuggestions,
    );

    // If we had to filter any, update the cache
    if (validSuggestions.length !== timestampedSuggestions.length) {
      scopes[cacheKey] = validSuggestions;
      await this.updateScopes(scopes);
    }

    // Return without the internal _timestamp property
    return validSuggestions.map(({ _timestamp, ...rest }) => rest);
  }

  public async getRaw(
    cacheKey: CacheKeyUnion,
  ): Promise<TimestampedQuerySuggestionResult[]> {
    const scopes = await this.getScopes();
    return this.filterExpiredSuggestions(scopes[cacheKey] || []);
  }

  public async set(
    cacheKey: CacheKeyUnion,
    suggestions: QuerySuggestionResult[],
    timestamp = Date.now(),
  ): Promise<void> {
    const dynamicSuggestions = suggestions.filter(
      (suggestion) => !suggestion.isStaticSuggestion,
    );

    const timestampedSuggestions = dynamicSuggestions.map((suggestion) => ({
      ...suggestion,
      _timestamp: timestamp,
    }));

    const scopes = await this.getScopes();
    const existingSuggestions = scopes[cacheKey] || [];

    const validExistingSuggestions =
      this.filterExpiredSuggestions(existingSuggestions);

    const refreshedSuggestions = this.applyStaggeredRefresh(
      validExistingSuggestions,
      timestampedSuggestions,
    );

    const trimmedSuggestions = this.applyFIFO(refreshedSuggestions);

    if (!scopes[cacheKey]) {
      scopes[cacheKey] = [];
    }

    scopes[cacheKey] = trimmedSuggestions;
    await this.updateScopes(scopes);

    metrics.stats("final-cache-size", trimmedSuggestions.length);
  }

  public async getLastQuerySuggestionsSyncMs(): Promise<number> {
    const cacheState = await this.cache.getAll();
    return cacheState[LAST_SYNC_KEY] || 0;
  }

  public async saveQuerySuggestionsSyncedMs(): Promise<void> {
    await this.cache.set(LAST_SYNC_KEY, Date.now());
  }

  public async clear(clearKey?: CacheKeyUnion): Promise<void> {
    const scopes = await this.getScopes();
    let updatedScopes: Record<string, TimestampedQuerySuggestionResult[]> =
      Object.assign({}, scopes);

    if (clearKey && clearKey !== GLOBAL_CACHE_KEY && scopes[clearKey]) {
      delete updatedScopes[clearKey];
    } else if (!clearKey) {
      updatedScopes = {
        [GLOBAL_CACHE_KEY]: [],
      };
    }

    await this.updateScopes(updatedScopes);
  }

  public async cleanupExpired(): Promise<number> {
    const scopes = await this.getScopes();
    let totalRemoved = 0;
    let totalScopes = 0;

    for (const key of Object.keys(scopes)) {
      try {
        const suggestions = scopes[key];
        if (!suggestions || !suggestions.length) continue;

        totalScopes++;

        const validSuggestions = this.filterExpiredSuggestions(suggestions);
        const removed = suggestions.length - validSuggestions.length;

        if (removed > 0) {
          totalRemoved += removed;
          scopes[key] = validSuggestions;
        }
      } catch (e) {
        logger.warn(`Error cleaning cache key ${key}`, e);
      }
    }

    if (totalRemoved > 0) {
      await this.updateScopes(scopes);
    }

    metrics.counter("total-expired-removed", totalRemoved);
    metrics.stats("total-scopes", totalScopes);

    return totalRemoved;
  }
}
